import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaCommentAlt, FaPrint } from 'react-icons/fa';
import { FaCheck } from 'react-icons/fa6';
import { HiMiniDocumentCheck, HiOutlineEye } from 'react-icons/hi2';
import { LuSend } from 'react-icons/lu';
import { MdOutlineModeEditOutline, MdScoreboard } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { ActionIcon, Button, Text, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import CollapsibleScorePeriodPanel from '../../../components/CollapsibleScorePeriodPanel';
import DeletePopover from '../../../components/buttons/DeletePopover';
import StatusDropdown from '../../../components/forms/StatusDropdown';
import { Loader } from '../../../components/loader/SpinnerLogo';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import { useFiltersPR } from '../../../components/providers/FiltersProvider';
import CustomTable from '../../../components/table/CustomTable';
import { FilterDrawerView } from '../../../components/table/components/TableFilter';
import useContractBlockedStates from '../../../hooks/api-calls/contract/useContractBlockedStates';
import useContractCommissionStates from '../../../hooks/api-calls/contract/useContractCommissionStates';
import useContractIncidenceStates from '../../../hooks/api-calls/contract/useContractIncidenceStates';
import useContractInvoiceStates from '../../../hooks/api-calls/contract/useContractInvoicingStates';
import useContractProductsStates from '../../../hooks/api-calls/contract/useContractProductsStates';
import useContractStates from '../../../hooks/api-calls/contract/useContractStates';
import useKoReasons from '../../../hooks/api-calls/useKoReasons';
import useDepartmentBlockedContractStatuses from '../../../hooks/useDepartmentBlockedContractStatuses';
import useBlockedEntryProductStatuses from '../../../hooks/useBlockedEntryProductStatuses';
import useHandleDownloadDocument from '../../../hooks/useDownloadDocument';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import Page from '../../../layout/Page/Page';
import { RootState } from '../../../redux/store';
import { setContractListNavigation } from '../../../redux/contractProfileSlice';
import { menuRoutes } from '../../../router/menu';
import { ContractService } from '../../../services/contract/contractService';
import { ContractsApiResponse } from '../../../type/entities/contract-type';
import { fixNumber } from '../../../utils/formatNumber';
import { isProductStatusLocked } from '../../../utils/contractProductStatusLock';
import { isContractInBlockedEntryFlow, isSubsidisedProductType } from '../../../utils/contractProductType';
import { buildContractListReturnToFromFilters, getContractProfileNavigationState } from '../../../utils/contractNavigation';
import { AddCommentModal } from '../../clients/list/components/AddCommentModal';
import ScoreModalForm from '../components/ScoreModalForm';
import CallModalForm from '../components/CallModalForm';
import ContractsFilters from './ContractFilters';
import { BiSolidPhoneCall } from "react-icons/bi";

export const contractColumnOrder = [
    'id',
    'code',
    'groupCode',
    'currentContractStatus',
    'currentContractStatusDate',
    'currentProductStatus',
    'currentContractProductStatusDate',
    'currentBlockedStatus',
    'currentBlockedStatusDate',
    'currentInvoicingStatus',
    'currentInvoicingStatusDate',
    'currentIncidence',
    'currentIncidenceStatus',
    'currentIncidenceStatusColor',
    'currentIncidenceStatusDate',
    'currentIncidenceStatusId',
    'currentCommissionStatus',
    'currentCommissionStatusColor',
    'currentCommissionStatusDate',
    'currentCommissionStatusId',
    'productType',
    'product',
    'order',
    'commercialName',
    'commercialLastName',
    'commercialId',
    'commercialNetworkName',
    'client',
    'clientCompanyName',
    'clientCifNif',
    'clientTelephone',
    'clientTelephoneSecondary',
    'clientMobile',
    'totalAmount',
    'createdAt',
    'invoiceId',
    'incidence',
    'complements',
    'isDraft',
    'isSigned',
    'isSignedDirectDebitMandate',
    'isSendSmsContractToSign',
    'canSendContractToSignature',
    'score',
];

interface ContractsListProps {
    mode?: 'default' | 'blocked';
    forcedCurrentProductStatuses?: string[];
    showStatusFilter?: boolean;
}

const ContractsList: React.FC<ContractsListProps> = ({ mode = 'default', forcedCurrentProductStatuses = [], showStatusFilter = true }) => {

    // HOOKS

    const { user } = useSelector((state: RootState) => state.auth);
    const { userCan } = usePrivilege();
    const { handleDownloadDocumentNewTab } = useHandleDownloadDocument();
    const { handleErrors } = useHandleErrors();
    const canChangeStatus = userCan('edit_contracts', 'contracts') && userCan('get_statuses_for_select', 'statuses');
    const departmentBlockedContractStatusIds = useDepartmentBlockedContractStatuses();
    const { getContractStatesList, isLoadingContractStates } = useContractStates({ active: true, action: 'edit', entity: 'contract' });
    const { getContractProductsStatesList, isLoadingContractProductsStates } = useContractProductsStates({ active: true, action: 'edit', entity: 'contract_product' });
    const { getContractInvoiceStatesList, isLoadingContractInvoiceStates } = useContractInvoiceStates({ active: true, action: 'edit', entity: 'contract_invoicing' });
    const { getContractIncidenceStatesList, isLoadingContractIncidenceStates } = useContractIncidenceStates({ active: true, action: 'edit', entity: 'contract_incidence' });
    const { getContractBlockedStatesList, isLoadingContractBlockedStates } = useContractBlockedStates({ active: true, action: 'edit', entity: 'contract_blocked' });
    const { getContractCommissionStatesList, isLoadingContractCommissionStates } = useContractCommissionStates({ active: true, action: 'edit', entity: 'contract_commission' });
    const blockedEntryProductStatusIds = useBlockedEntryProductStatuses();
    const { koReasonsOptions } = useKoReasons();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const dispatch = useDispatch();
    const listLocation = useLocation();
    const contractProfileNavigationState = useMemo(
        () => getContractProfileNavigationState(listLocation.pathname, filters),
        [listLocation.pathname, filters]
    );
    const service = new ContractService();

    // STATES

    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [openScoreModal, setOpenScoreModal] = useState<boolean>(false);
    const [score, setScore] = useState<number>(0);
    const [phone, setPhone] = useState<string>('');
    const [phoneSecondary, setPhoneSecondary] = useState<string>('');
    const [mobile, setMobile] = useState<string>('');
    const [currentContractId, setCurrentContractId] = useState<string>('');
    const [currentClientId, setCurrentClientId] = useState<string>('');
    const [openCommentModal, setOpenCommentModal] = useState<boolean>(false);
    const [openCallModal, setOpenCallModal] = useState<boolean>(false);
    const [rowId, setRowId] = useState<string>('');
    const [loadingExport, setLoadingExport] = useState<boolean>(false);
    const [isLoadingContractPdf, setLoadingContractPdf] = useState<boolean>(false);

    useEffect(() => {
        if (mode !== 'blocked') return;
        const expectedStatuses = forcedCurrentProductStatuses.length > 0
            ? forcedCurrentProductStatuses
            : ['__NO_BLOCKED_STATUS_CONFIGURED__'];
        const currentProductStatuses = filters.filter_filters?.current_product_status ?? null;
        if (JSON.stringify(currentProductStatuses) !== JSON.stringify(expectedStatuses)) {
            updateFilters({ current_product_status: expectedStatuses });
        }
    }, [mode, forcedCurrentProductStatuses, filters.filter_filters?.current_product_status, updateFilters]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listContracts(filters);
        return response.getResponseData() as ContractsApiResponse;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]));

    useEffect(() => {
        const contracts = data?.data;
        if (!Array.isArray(contracts) || contracts.length === 0) {
            return;
        }

        const contractIds = contracts
            .map((contract: { id?: string }) => contract.id)
            .filter((contractId): contractId is string => Boolean(contractId));

        if (!contractIds.length) {
            return;
        }

        dispatch(setContractListNavigation({
            contractIds,
            returnTo: buildContractListReturnToFromFilters(listLocation.pathname, filters),
            sourceListPath: listLocation.pathname,
        }));
    }, [data?.data, dispatch, filters, listLocation.pathname]);

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR CONTRATO
     * @EN DELETE CONTRACT
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        const response = await (await service.deleteContract(id)).getResponseData();
        if (response.success) {
            toast.success('Contrato eliminado correctamente');
            refetch();
        } else {
            handleErrors(response);
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CAMBIAR ESTADO DE CONTRATO
     * @EN CHANGE CONTRACT STATUS
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleStateChange = async (id: string, stateId: string, comment: string, entityId: string, entityName: string, koReasonId?: string) => {
        var response = { success: false };
        switch (entityName) {
            case 'contract':
                try {
                    response = await (await service.changeContractStatus(id, stateId, comment, koReasonId)).getResponseData();
                    if (response.success) {
                        toast.success('Estado del contrato actualizado correctamente');
                    } else {
                        handleErrors(response);
                    }
                } catch (error) {
                    toast.error('Error al actualizar el estado');
                }
                break;
            case 'invoice':
                try {
                    response = await (await service.changeContractInvoicingStatus(id, stateId, entityId, comment, koReasonId)).getResponseData();
                    if (response.success) {
                        toast.success('Estado de la factura actualizado correctamente');
                    } else {
                        handleErrors(response);
                    }
                } catch (error) {
                    toast.error('Error al actualizar el estado');
                }
                break;
            case 'product':
                try {
                    response = await (await service.changeContractProductStatus(id, stateId, entityId, comment, koReasonId)).getResponseData();
                    if (response.success) {
                        toast.success('Estado del producto actualizado correctamente');
                    } else {
                        handleErrors(response);
                    }
                } catch (error) {
                    toast.error('Error al actualizar el estado del producto');
                }
                break;
            case 'incidence':
                try {
                    response = await (await service.changeContractIncidenceStatus(id, stateId, entityId, comment, koReasonId)).getResponseData();
                    if (response.success) {
                        toast.success('Estado de la incidencia actualizado correctamente');
                    } else {
                        handleErrors(response);
                    }
                } catch (error) {
                    toast.error('Error al actualizar el estado de la incidencia');
                }
                break;
            case 'blocked':
                try {
                    response = await (await service.changeContractBlockedStatus(id, stateId, comment, koReasonId)).getResponseData();
                    if (response.success) {
                        toast.success('Estado de bloqueado actualizado correctamente');
                    } else {
                        handleErrors(response);
                    }
                } catch (error) {
                    toast.error('Error al actualizar el estado de bloqueado');
                }
                break;
            case 'commission':
                try {
                    response = await (await service.changeContractCommissionStatus(id, stateId, comment, koReasonId)).getResponseData();
                    if (response.success) {
                        toast.success('Estado de la comisión actualizado correctamente');
                    } else {
                        handleErrors(response);
                    }
                } catch (error) {
                    toast.error('Error al actualizar el estado de la comisión');
                }
                break;
            default:
                break;
        }
        refetch();
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR MULTIPLES CONTRATOS
     * @EN DELETE MULTIPLE CONTRACTS
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        const response = await (await service.deleteMultiContracts(ids)).getResponseData();
        if (response.success) {
            toast.success('Contratos eliminados correctamente');
            refetch();
        } else {
            handleErrors(response);
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES EXPORTAR CONTRATOS
     * @EN EXPORT CONTRACTS
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleExport = async (ids: string[]) => {
        setLoadingExport(true);
        try {
            const response = (await (new ContractService).exportContracts({ filter_filters: { contracts_to_export: ids } }));
            if (response) {
                const fileData = response.getResponseData();
                const blob = new Blob([fileData]);
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `contratos_${moment().format('DD-MM-YYYY')}.xlsx`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else {
                toast.error('Error al exportar los contratos a excel');
            }
        } catch (error: any) {
            toast.error(error.message);
        } finally {
            setLoadingExport(false);
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ENVIAR CONTRATO POR SMS
     * @EN SEND CONTRACT BY SMS
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleSendContractSms = async (id: string) => {
        try {
            const response = await (await service.sendContractSms(id)).getResponseData();
            if (response.success) {
                toast.success('Comunicación de firma enviada correctamente');
                refetch();
            } else {
                handleErrors(response);
            }
        } catch (error) {
            toast.error('Error al enviar la comunicación de firma');
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    const handleGenerateContractPdf = async (contractId: string, contractCode: string) => {
        setLoadingContractPdf(true);
        try {
            await handleDownloadDocumentNewTab(service.generateContractPdf, contractId, `${contractCode}_contrato.pdf`);
        } finally {
            setLoadingContractPdf(false);
        }
    };

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ORDENAR DATOS PARA QUE COINCIDAN CON EL ORDEN DE LAS COLUMNAS
     * @EN ORDER DATA TO MATCH COLUMN ORDER
     */
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        contractColumnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    //-------------------------------------------------------------------------------------------------------------------------------

    // RENDER

    return (
        <Page container='fluid'>
            {!isLoadingContractStates && !isLoadingContractProductsStates && !isLoadingContractInvoiceStates &&
                !isLoadingContractIncidenceStates && !isLoadingContractBlockedStates && !isLoadingContractCommissionStates && data !== undefined
                ? (
                    <>
                        {/* Score Period Panel */}

                        { mode !== 'blocked' ? (
                                <CollapsibleScorePeriodPanel userId={user?.id} className='mb-4' />
                            ) : (
                                <></>
                            )
                        }

                        <CustomTable
                            largeTable
                            id={'contracts-table'}
                            columnOrder={contractColumnOrder}
                            data={orderedData()}
                            isLoading={loading}
                            columnsNotShown={[
                                'currentContractProductStatusDate',
                                'currentBlockedStatusDate',
                                'currentInvoicingStatusDate',
                                'commercialId',
                                'clientCompanyName',
                                'clientCifNif',
                                'currentContractStatusDate',
                                'invoiceId',
                                'incidence',
                                'complements',
                                'isDraft',
                                'clientTelephone',
                                'clientTelephoneSecondary',
                                'clientMobile',
                                'currentIncidence',
                                'currentIncidenceStatus',
                                'currentIncidenceStatusColor',
                                'currentIncidenceStatusDate',
                                'currentCommissionStatusColor',
                                'currentCommissionStatusDate',
                                'currentCommissionStatus',
                                'isSigned',
                                'isSignedDirectDebitMandate',
                                'canSendContractToSignature',
                                'score',
                                'commercialLastName',
                            ]}
                            overrideColumns={[
                                {
                                    key: 'code',
                                    render: (row: any) => {
                                        return (
                                            userCan('get_contracts', 'contracts')
                                                ? (
                                                    <Link
                                                        to={`${menuRoutes.contracts.path}/${row.row.original.id}/profile${menuRoutes.contracts.profile.info}`}
                                                        state={contractProfileNavigationState}
                                                    >
                                                        <Text className='font-bold text-primary'>{row.row.original.code}</Text>
                                                    </Link>
                                                )
                                                : <Text className='font-bold text-primary'>{row.row.original.code}</Text>
                                        );
                                    }
                                },
                                {
                                    key: 'order',
                                    render: (row: any) => {
                                        const order = row.row.original.order;
                                        return (
                                            order
                                                ? userCan('get_orders', 'orders')
                                                    ? (
                                                        <Link to={`${menuRoutes.orders.path}/${order?.id}/profile${menuRoutes.orders.profile.info}`}>
                                                            <Text className='font-bold text-primary'>{order?.code}</Text>
                                                        </Link>
                                                    )
                                                    : <Text className='font-bold text-primary'>{order?.code}</Text>
                                                : <Text>N/A</Text>
                                        )
                                    },
                                },
                                {
                                    key: 'groupCode',
                                    render: (row: any) => {
                                        return row.row.original?.groupCode || 'N/A';
                                    },
                                },
                                {
                                    key: 'commercialName',
                                    render: (row: any) => {
                                        const commercialName = row.row.original.commercialName + (row.row.original.commercialLastName ? ` ${row.row.original.commercialLastName}` : '');
                                        return (
                                            commercialName
                                                ? userCan('get_user', 'user')
                                                    ? (
                                                        (userCan('admin_user', 'user')) ?
                                                        <Link to={`${menuRoutes.users.path}/${row.row.original.commercialId}/profile${menuRoutes.users.profile.info}`}>
                                                            <Text className='font-bold text-primary'>{commercialName}</Text>
                                                        </Link>
                                                        :
                                                        <Text className='font-bold text-primary'>{commercialName}</Text>
                                                    )
                                                    : <Text>{commercialName}</Text>
                                                : <Text>N/A</Text>
                                        )
                                    },
                                },
                                {
                                    key: 'client',
                                    render: (row: any) => {
                                        const client = row.row.original?.client;
                                        const companyName =
                                        row.row.original?.clientCompanyName ||
                                        row.row.original?.clientCifNif ||
                                        'N/A';

                                        const canViewClient = userCan('get_clients', 'clients');
                                        const canViewComments = userCan('get_clients_comments', 'clients');

                                        if (!client) 
                                            {
                                                return <span className="font-bold text-primary">{companyName}</span>;
                                            }

                                        return (
                                            <div>
                                                {canViewClient ? (
                                                    (userCan('show', 'clients') && userCan('admin_clients', 'clients')) ?<Link
                                                        to={`${menuRoutes.clients.path}/${client.id}/profile/info`}
                                                        state={{
                                                            fromContract: true,
                                                            organisingCorporationId: row.row.original?.organisingCorporation?.id || row.row.original?.organisingCorporationId || null,
                                                            returnToContractPath: `${menuRoutes.contracts.path}/${row.row.original?.id}/profile${menuRoutes.contracts.profile.info}`,
                                                        }}
                                                        className="font-bold text-primary cursor-pointer d-block"
                                                    >
                                                        {companyName}
                                                    </Link> : <span className="font-bold text-primary d-block">{companyName}</span>
                                                ) : (
                                                    <span className="font-bold text-primary d-block">
                                                        {companyName}
                                                    </span>
                                                )}

                                            {row.row.original?.clientCifNif && (
                                            <div className="d-block">{row.row.original.clientCifNif}</div>
                                            )}

                                            {canViewComments && client.lastComment && (
                                            <Tooltip
                                                size="sm"
                                                content={<div className="tooltip-container">{client.lastComment}</div>}
                                                placement="top"
                                                color="invert"
                                            >
                                                <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="text"
                                                className="hover:!border-gray-900 hover:text-gray-700"
                                                >
                                                <FaCommentAlt className="h-4 w-4" size={20} />
                                                </ActionIcon>
                                            </Tooltip>
                                            )}
                                        </div>
                                        );
                                    }
                                },
                                {
                                    key: 'product',
                                    render: (row: any) => {
                                        const product = row.row.original?.product;
                                        const complements = row.row.original?.complements;
                                        return (
                                            <div className='flex flex-col'>
                                                {product ?
                                                    ( userCan('show', 'products') || userCan('admin_products', 'products') ? <Link
                                                        to={`${menuRoutes.products.path}?filter_filters=%7B"active"%3Atrue%2C"search_text"%3A"${product.name}"%7D&filter_order=%5B%5D&page=1&limit=50`}
                                                        className='font-bold text-primary'
                                                    >
                                                        {product.name}
                                                    </Link> : <span className="font-bold text-primary d-block">{product.name}</span>)
                                                    : 'N/A'}

                                                {product && complements && complements.length >= 1 && (
                                                    <div className='flex flex-col gap-1 mt-2 font-semibold'>
                                                        <Text className='font-semibold text-md'>Complementos:</Text>
                                                        <ul className='indent-4'>
                                                            {complements.map((complement: any, index: number) => (
                                                                <li
                                                                    key={product.id + index}
                                                                    className='text-md'
                                                                >
                                                                    <Text className='text-xs text-nowrap'>- {complement.complements.name} ({complement.amount}€)</Text>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>)
                                                }
                                            </div>
                                        )
                                    }
                                },
                                {
                                    key: 'productType',
                                    render: (row: any) => { return row.row.original?.productType?.name }
                                },
                                {
                                    key: 'currentContractStatus',
                                    render: (row: any) => {
                                        const contractStatusDate = row.row.original?.currentContractStatusDate?.date;
                                        const contractStatus = row.row.original?.currentContractStatus;
                                        const contractId = row.row.original?.id;
                                        const isDraftContract = Boolean(row.row.original?.isDraft);
                                        const contractStatesOptions = getContractStatesList(null, contractStatus?.id)
                                            .filter((state: any) => isDraftContract || !state.draftStatus);
                                        return (
                                            <div className='flex flex-col gap-2 justify-content-center text-center' key={row.row.original.id}>
                                                { (contractId && contractStatusDate && contractStatus) ? (
                                                    <StatusDropdown
                                                        entityId={row.row.original?.id}
                                                        disabled={!contractId || !canChangeStatus || isDraftContract}
                                                        title="¿Estás seguro de cambiar el estado del contrato?"
                                                        statesOptions={contractStatesOptions}
                                                        currentState={contractStatus}
                                                        currentStateDate={contractStatusDate}
                                                        koReasonsOptions={koReasonsOptions}
                                                        koReasonStatusEntity="contract"
                                                        handleStateChange={(entityId: string, statusId: string, comment: string, koReasonId?: string) =>
                                                            handleStateChange(entityId, statusId, comment, entityId, 'contract', koReasonId)
                                                        }
                                                    />
                                                ) : "N/A"}
                                            </div>
                                        );
                                    }
                                },
                                {
                                    key: 'currentInvoicingStatus',
                                    render: (row: any) => {
                                        const invoiceStatusDate = row.row.original?.currentInvoicingStatusDate?.date;
                                        const invoiceStatus = row.row.original?.currentInvoicingStatus;
                                        const invoiceId = row.row.original?.invoiceId;
                                        return (
                                            <div className='flex flex-col gap-2 justify-content-center text-center' key={row.row.original.id}>
                                                {invoiceStatus ? (
                                                    <StatusDropdown
                                                        disabled={!invoiceId || row.row.original?.isDraft || !canChangeStatus}
                                                        entityId={row.row.original?.id}
                                                        title="¿Estás seguro de cambiar el estado de la factura?"
                                                        statesOptions={getContractInvoiceStatesList(null, invoiceStatus.id)}
                                                        currentState={invoiceStatus}
                                                        currentStateDate={invoiceStatusDate}
                                                        koReasonsOptions={koReasonsOptions}
                                                        koReasonStatusEntity="contract_invoicing"
                                                        handleStateChange={(entityId: string, statusId: string, comment: string, koReasonId?: string) =>
                                                            handleStateChange(entityId, statusId, comment, invoiceId, 'invoice', koReasonId)
                                                        }
                                                    />
                                                ) : "N/A"}
                                            </div>
                                        );
                                    }
                                },
                                {
                                    key: 'currentProductStatus',
                                    render: (row: any) => {
                                        const productStatusDate = row.row.original?.currentContractProductStatusDate?.date;
                                        const productStatus = row.row.original?.currentProductStatus;
                                        const product = row.row.original?.product;
                                        const isProductLocked = isProductStatusLocked(
                                            row.row.original?.currentContractStatus?.id,
                                            departmentBlockedContractStatusIds
                                        );
                                        return (
                                            <div className='flex flex-col gap-2 justify-content-center text-center' key={row.row.original.id}>
                                                {(productStatusDate && productStatus) ? (
                                                    <StatusDropdown
                                                        disabled={!product?.id || row.row.original?.isDraft || !canChangeStatus || isProductLocked}
                                                        entityId={row.row.original?.id}
                                                        title="¿Estás seguro de cambiar el estado del producto?"
                                                        statesOptions={getContractProductsStatesList(null, productStatus.id)}
                                                        currentState={productStatus}
                                                        currentStateDate={productStatusDate}
                                                        koReasonsOptions={koReasonsOptions}
                                                        koReasonStatusEntity="contract_product"
                                                        handleStateChange={(entityId: string, statusId: string, comment: string, koReasonId?: string) => {
                                                            handleStateChange(entityId, statusId, comment, product.id, 'product', koReasonId);
                                                        }
                                                        }
                                                    />
                                                ) : "N/A"}
                                            </div>
                                        );
                                    }
                                },
                                {
                                    key: 'currentBlockedStatus',
                                    render: (row: any) => {

                                        if (!isSubsidisedProductType(row.row.original?.productType)) {
                                            return <span className="text-center block">N/A</span>;
                                        }

                                        const blockedStatus = row.row.original?.currentBlockedStatus;
                                        const blockedStatusDate = row.row.original?.currentBlockedStatusDate?.date;
                                        const productStatus = row.row.original?.currentProductStatus;
                                        const inBlockedFlow = isContractInBlockedEntryFlow(productStatus?.id, blockedEntryProductStatusIds);

                                        return (
                                            <div className='flex flex-col gap-2 justify-content-center text-center' key={row.row.original.id}>
                                                { blockedStatus  && blockedStatusDate ? (
                                                <StatusDropdown
                                                    disabled={row.row.original?.isDraft || !canChangeStatus || !inBlockedFlow}
                                                    entityId={row.row.original?.id}
                                                    title="¿Estás seguro de cambiar el estado de bloqueado?"
                                                    statesOptions={getContractBlockedStatesList(null, blockedStatus.id, false)}
                                                    currentState={blockedStatus}
                                                    currentStateDate={blockedStatusDate}
                                                    koReasonsOptions={koReasonsOptions}
                                                    koReasonStatusEntity="contract_blocked"
                                                    handleStateChange={(entityId: string, statusId: string, comment: string, koReasonId?: string) =>
                                                        handleStateChange(entityId, statusId, comment, entityId, 'blocked', koReasonId)
                                                    }
                                                />
                                                ) : "N/A"}
                                            </div>
                                        );
                                    }
                                },
                                {
                                    key: 'currentIncidenceStatusId',
                                    render: (row: any) => {
                                        const rawIncidenceStatus = row.row.original?.currentIncidenceStatus;
                                        const incidenceStatus = rawIncidenceStatus && typeof rawIncidenceStatus === 'object' && rawIncidenceStatus.id
                                            ? { ...rawIncidenceStatus, date: row.row.original?.currentIncidenceStatusDate?.date ?? row.row.original?.currentIncidenceStatusDate }
                                            : (row.row.original?.currentIncidenceStatusId ? {
                                                id: row.row.original.currentIncidenceStatusId,
                                                color: row.row.original.currentIncidenceStatusColor,
                                                date: row.row.original?.currentIncidenceStatusDate?.date ?? row.row.original?.currentIncidenceStatusDate,
                                                name: row.row.original?.currentIncidenceStatus,
                                            } : null);
                                        const incidence = row.row.original?.currentIncidence;
                                        return (
                                            <div className='flex flex-col gap-2 justify-content-center text-center'>
                                                {(incidenceStatus && incidenceStatus.id) ? (
                                                    <StatusDropdown
                                                        disabled={!incidence || row.row.original?.isDraft || !canChangeStatus}
                                                        entityId={row.row.original?.id}
                                                        title="¿Estás seguro de cambiar el estado de la incidencia?"
                                                        statesOptions={getContractIncidenceStatesList(null, incidenceStatus.id, false)}
                                                        currentState={incidenceStatus}
                                                        currentStateDate={incidenceStatus.date}
                                                        koReasonsOptions={koReasonsOptions}
                                                        koReasonStatusEntity="contract_incidence"
                                                        handleStateChange={(entityId: string, statusId: string, comment: string, koReasonId?: string) =>
                                                            handleStateChange(entityId, statusId, comment, incidence?.id ?? '', 'incidence', koReasonId)
                                                        }
                                                    />
                                                ) : "N/A"}
                                            </div>
                                        );
                                    }
                                },
                                {
                                    key: 'currentCommissionStatusId',
                                    render: (row: any) => {
                                        const commissionStatus = {
                                            'id': row.row.original?.currentCommissionStatusId,
                                            'color': row.row.original?.currentCommissionStatusColor,
                                            'date': row.row.original?.currentCommissionStatusDate,
                                            'name': row.row.original?.currentCommissionStatus,
                                        }
                                        return (
                                            <div className='flex flex-col gap-2 justify-content-center text-center'>
                                                {(commissionStatus && commissionStatus.id) ? (
                                                    <StatusDropdown
                                                        disabled={row.row.original?.isDraft || !canChangeStatus}
                                                        entityId={row.row.original?.id}
                                                        title="¿Estás seguro de cambiar el estado de la comisión?"
                                                        statesOptions={getContractCommissionStatesList(null, commissionStatus.id)}
                                                        currentState={commissionStatus}
                                                        currentStateDate={commissionStatus.date}
                                                        koReasonsOptions={koReasonsOptions}
                                                        koReasonStatusEntity="contract_commission"
                                                        handleStateChange={(entityId: string, statusId: string, comment: string, koReasonId?: string) =>
                                                            handleStateChange(entityId, statusId, comment, '', 'commission', koReasonId)
                                                        }
                                                    />
                                                ) : "N/A"}
                                            </div>
                                        );
                                    }
                                },
                                {
                                    key: 'totalAmount',
                                    render: (row: any) => {
                                        return (typeof row.row.original?.totalAmount === 'number' && !isNaN(row.row.original?.totalAmount)) ? `${fixNumber(row.row.original?.totalAmount)}€` : 'N/A';
                                    }
                                },
                                {
                                    key: 'isSendSmsContractToSign',
                                    render(row: any) {
                                        const isEnabledFlag = (value: any) => value === true || value === 1 || value === '1' || value === 'true' || value === 't';
                                        const hasContractSigned = isEnabledFlag(row.row.original.isSigned);
                                        const hasDirectDebitSigned = isEnabledFlag(row.row.original.isSignedDirectDebitMandate);
                                        const hasAnySignedDocument = hasContractSigned || hasDirectDebitSigned;
                                        const hasSignatureCommunication = isEnabledFlag(row.row.original.isSendSmsContractToSign);
                                        const showInitialSignatureSendAction = isEnabledFlag(row.row.original.canSendContractToSignature);
                                        const showSignatureResendAction = hasSignatureCommunication && !hasAnySignedDocument;
                                        const showSignatureSendAction = showInitialSignatureSendAction || showSignatureResendAction;

                                        if (!showSignatureSendAction && !hasAnySignedDocument && !hasSignatureCommunication) return null;

                                        const signedTooltip = hasContractSigned && hasDirectDebitSigned
                                            ? 'Contrato y autorización de domiciliación firmados'
                                            : hasDirectDebitSigned
                                                ? 'Autorización de domiciliación firmada'
                                                : 'Contrato firmado';
                                        const sendTooltip = hasSignatureCommunication
                                            ? 'Reenviar contrato a firma'
                                            : 'Enviar contrato a firma';

                                        return (
                                            <div className='flex justify-center'>
                                                {hasAnySignedDocument && (
                                                    <Tooltip
                                                        size="sm"
                                                        content={signedTooltip}
                                                        placement="top"
                                                        color="success"
                                                    >
                                                        <div><HiMiniDocumentCheck className="h-5 w-5" size={20} color='green' /></div>
                                                    </Tooltip>
                                                )}
                                                {!hasAnySignedDocument && hasSignatureCommunication && (
                                                    <Tooltip
                                                        size="sm"
                                                        content={'Contrato enviado por SMS/email para su firma'}
                                                        placement="top"
                                                        color="invert"
                                                    >
                                                        <div><FaCheck className="h-4 w-4" size={20} color='green' /></div>
                                                    </Tooltip>
                                                )}
                                                {showSignatureSendAction && (
                                                    <Tooltip
                                                        size="sm"
                                                        content={showSignatureResendAction ? 'Reenviar contrato a firma' : sendTooltip}
                                                        placement="top"
                                                        color="invert"
                                                    >
                                                        <div>
                                                            <LuSend
                                                                className={`${hasAnySignedDocument || hasSignatureCommunication ? 'ms-4 ' : ''}h-4 w-4 cursor-pointer`}
                                                                size={20}
                                                                onClick={() => { handleSendContractSms(row.row.original.id) }}
                                                            />
                                                        </div>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        );
                                    }
                                },
                            ]}
                            actions={[
                                {
                                    label: 'Realizar llamada',
                                    permissions: { group: 'calls', permission: 'realize_calls' },
                                    render: (row: any) => {
                                        const hasPhone = Boolean(row.clientTelephone && row.clientTelephone.trim() !== "");
                                        const hasPhoneSecondary = Boolean(row.clientTelephoneSecondary && row.clientTelephoneSecondary.trim() !== "");
                                        const hasMobile = Boolean(row.clientMobile && row.clientMobile.trim() !== "");
                                        const hasAnyPhone = hasPhone || hasPhoneSecondary || hasMobile;

                                        return (
                                            <Tooltip
                                                size="sm"
                                                content={
                                                    hasAnyPhone
                                                        ? 'Realizar llamada'
                                                        : 'El cliente no posee número de teléfono'
                                                }
                                                placement="top"
                                                color={hasAnyPhone ? "invert" : "danger"}
                                            >
                                                <ActionIcon
                                                    as="span"
                                                    size="sm"
                                                    variant="outline"
                                                    className={
                                                        hasAnyPhone
                                                            ? "hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                                            : "opacity-50 cursor-not-allowed"
                                                    }
                                                    disabled={!hasAnyPhone}
                                                    onClick={() => {
                                                        if (!hasAnyPhone) return;
                                                        setCurrentContractId(row.id);
                                                        setRowId(row.id);
                                                        setCurrentClientId(row.client?.id ?? '');
                                                        setPhone(row.clientTelephone);
                                                        setPhoneSecondary(row.clientTelephoneSecondary);
                                                        setMobile(row.clientMobile);
                                                        setOpenCallModal(true);
                                                    }}
                                                >
                                                    <BiSolidPhoneCall className="h-4 w-4" size={20} />
                                                </ActionIcon>
                                            </Tooltip>
                                        );
                                    }
                                },
                                {
                                    label: 'Modificar puntuación',
                                    permissions: { group: 'contracts', permission: 'update_contracts_score' },
                                    render: (row: any) => {
                                        return (
                                            <Tooltip
                                                size="sm"
                                                content={'Modificar puntuación'}
                                                placement="top"
                                                color="invert"
                                            >
                                                <ActionIcon
                                                    as="span"
                                                    size="sm"
                                                    variant="outline"
                                                    className="hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                                    onClick={() => { setRowId(row.id); setScore(row.score); setOpenScoreModal(true) }}
                                                >
                                                    <MdScoreboard className="h-4 w-4" size={20} />
                                                </ActionIcon>
                                            </Tooltip>
                                        );
                                    }
                                },
                                {
                                    label: 'Generar Contrato',
                                    permissions: { group: 'contracts', permission: 'print_contract' },
                                    render: (row: any) => {
                                        if (row.isDraft) return null;
                                        return (
                                            <Tooltip
                                                size="sm"
                                                content={row?.isSigned ? 'Descargar PDF del contrato firmado' : 'Descargar PDF del contrato'}
                                                placement="top"
                                                color="invert"
                                            >
                                                <ActionIcon
                                                    as="span"
                                                    size="sm"
                                                    variant="outline"
                                                    className={`hover:!border-gray-900 hover:text-gray-700 ${isLoadingContractPdf ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                                                    disabled={isLoadingContractPdf}
                                                    onClick={() => {
                                                        if (isLoadingContractPdf) return;
                                                        handleGenerateContractPdf(row.id, row.code);
                                                    }}
                                                >
                                                    {isLoadingContractPdf
                                                        ? <span className="h-4 w-4 inline-block rounded-full border-2 border-gray-400 border-t-transparent animate-spin" />
                                                        : row?.isSigned
                                                            ? <HiMiniDocumentCheck className="h-4 w-4" size={20} />
                                                            : <FaPrint className="h-4 w-4" size={20} />
                                                    }
                                                </ActionIcon>
                                            </Tooltip>
                                        );
                                    },
                                },
                                {
                                    label: 'Add Comment',
                                    permissions: { group: 'contracts', permission: 'create_contract_comments' },
                                    render: (row: any) => {
                                        return (
                                            <Tooltip
                                                size="sm"
                                                content={'Añadir comentario'}
                                                placement="top"
                                                color="invert"
                                            >
                                                <ActionIcon
                                                    as="span"
                                                    size="sm"
                                                    variant="outline"
                                                    className="hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                                    onClick={() => { setRowId(row.id); setOpenCommentModal(true) }}
                                                >
                                                    <FaCommentAlt className="h-4 w-4" size={20} />
                                                </ActionIcon>
                                            </Tooltip>
                                        );
                                    },
                                },
                                {
                                    label: 'View',
                                    permissions: { group: 'contracts', permission: 'get_contracts' },
                                    render: (row: any) => {
                                        return (
                                            <Tooltip
                                                size="sm"
                                                content={'Ver detalles'}
                                                placement="top"
                                                color="invert"
                                            >
                                                <Link
                                                    to={`${menuRoutes.contracts.path}/${row.id}/profile${menuRoutes.contracts.profile.info}`}
                                                    state={contractProfileNavigationState}
                                                >
                                                    <ActionIcon
                                                        as="span"
                                                        size="sm"
                                                        variant="outline"
                                                        className="hover:!border-gray-900 hover:text-gray-700"
                                                    >
                                                        <HiOutlineEye className="h-4 w-4" size={20} />
                                                    </ActionIcon>
                                                </Link>
                                            </Tooltip>
                                        );
                                    },
                                },
                                {
                                    label: 'Edit',
                                    permissions: { group: 'contracts', permission: 'edit_contracts' },
                                    render: (row: any) => {
                                        return (
                                            <Tooltip
                                                size="sm"
                                                content={'Editar contrato'}
                                                placement="top"
                                                color="invert"
                                            >
                                                <Link to={`${menuRoutes.contracts.path}/${row.id}/edit`}>
                                                    <ActionIcon
                                                        as="span"
                                                        size="sm"
                                                        variant="outline"
                                                        className="hover:!border-gray-900 hover:text-gray-700"
                                                    >
                                                        <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                                    </ActionIcon>
                                                </Link>
                                            </Tooltip>
                                        );
                                    },
                                },
                                {
                                    label: 'Delete',
                                    permissions: { group: 'contracts', permission: 'delete_contracts' },
                                    render: (row: any) => {
                                        return (
                                            <Tooltip
                                                size="sm"
                                                content={'Eliminar'}
                                                placement="top"
                                                color="invert"
                                            >
                                                <div>
                                                    <DeletePopover
                                                        title={`Eliminar contrato`}
                                                        description={`¿Estás seguro de que deseas eliminar el contrato ${row.code}?`}
                                                        onDelete={() => handleDelete(row.id)}
                                                    />
                                                </div>
                                            </Tooltip>
                                        );
                                    },
                                },
                            ]}
                            multiActions={[
                                {
                                    label: 'Exportar',
                                    permissions: { group: 'contracts', permission: 'export_contracts' },
                                    render: (selectedRows: any) => {
                                        return (
                                            <Button
                                                variant={'solid'}
                                                color={'secondary'}
                                                size={'sm'}
                                                onClick={() => handleExport(selectedRows)}
                                                className='p-3 text-nowrap'
                                                disabled={loadingExport}
                                                isLoading={loadingExport}
                                            >
                                                Exportar
                                            </Button>
                                        )
                                    },
                                },
                            ]}
                            handleMultipleDelete={handleMultiDelete}
                            multiDeletePermission={{ group: 'contracts', action: 'delete_contracts' }}
                            filters={filters}
                            updateFilters={updateFilters}
                            updateFilterOrder={updateFilterOrder}
                            defaultOrder={filters.filter_order || undefined}
                            paginationData={{
                                pageSize: filters.limit,
                                currentPage: filters.page,
                                pageCount: (data as ContractsApiResponse) ? data.lastPage : 1,
                                totalCount: data?.totalRegisters,
                                handlePagination: updatePage,
                                handlePerPage: updatePageSize,
                            }}
                            toggleFilters={() => setOpenFilters(!openFilters)}
                        />
                    </>
                )
                : (<Loader height="70vh" />)
            }

            <AddCommentModal service={(new ContractService()).addComment} id={rowId} isOpen={openCommentModal} onClose={() => setOpenCommentModal(false)} refetch={refetch} />

            <CallModalForm isOpen={openCallModal} onClose={() => setOpenCallModal(false)} refetch={refetch} contractId={currentContractId} currentClientId={currentClientId} phone={phone} phoneSecondary={phoneSecondary} mobile={mobile} />

            <ScoreModalForm isOpen={openScoreModal} onClose={() => setOpenScoreModal(false)} refetch={refetch} id={rowId} score={score} setScore={setScore} />

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Contratos'}>
                <ContractsFilters
                    filters={filters}
                    updateFilters={updateFilters}
                    resetFilters={resetFilters}
                    showStatusFilter={showStatusFilter}
                />
            </FilterDrawerView>
        </Page>
    );
};

export default ContractsList;
